import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import { ReactReduxContext } from 'react-redux';
import type { DocumentPdfViewerHandle } from '@/components/ora/Documents/DocumentPdfViewer/DocumentPdfViewer';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import type { ChatDocumentViewerCurrent } from '@/providers/ChatDocumentViewerContext';
import { INSTANCE_SLUGS } from '../instances/instanceSlugs';
import { isProfileInInstance } from '../instances/useInstance';
import { getLectureTree } from '../services/lectures';
import type { RootState } from '../store/store';
import type { LectureTreeNode } from '../types/Lecture';
import { MCGRAW_HILL_TEXTBOOK_EXAM_IDS } from './mcgrawHill';
import { usePlainOraViewFlag } from './plainOraView';
import {
  mcgrawHillDocumentTitle,
  useMcGrawHillDocumentTitle,
} from './mcgrawHillChapter';
import McGrawHillChapterNav, {
  mcgrawHillChapters,
  type McGrawHillContentsChapter,
} from './McGrawHillChapterNav';
import {
  MCGRAW_HILL_REFERENCES,
  mcgrawHillReferenceById,
} from './mcgrawHillReferences';
import { MCGRAW_HILL_SUBCHAPTERS } from './mcgrawHillSubchapters';

/**
 * mcgraw-hill (ORA-1247): the open document's book, whichever exam is
 * active. A chapter of a McGraw Hill textbook shows that book's contents
 * beside it: a Katzung lecture or Katzung reference chapter (K1–K15)
 * Katzung's, a Harrison's reference chapter Harrison's. Anything else (a
 * study, the handbook, the student's own upload) shows no contents.
 */
interface McGrawHillContents {
  chapters: McGrawHillContentsChapter[];
  activeKey: string;
  /** What a chapter's key opens: a lecture, or a reference document. */
  kind: 'lecture' | 'reference';
  /** The textbook exam a lecture chapter belongs to. */
  examId?: string;
}

const noStoreSubscribe = () => () => undefined;
/** A textbook's lecture trees, fetched once per exam (never into the store). */
const fetchedTrees = new Map<string, Promise<LectureTreeNode[] | null>>();

const fetchTextbookTree = (examId: string) => {
  let tree = fetchedTrees.get(examId);

  if (!tree) {
    tree = getLectureTree(examId).then(
      (result) => result.tree.nodes,
      () => {
        // Let a later open try again.
        fetchedTrees.delete(examId);
        return null;
      }
    );

    fetchedTrees.set(examId, tree);
  }

  return tree;
};

/** Test seam: forget the fetched trees. */
export const resetMcGrawHillContentsCache = (): void => fetchedTrees.clear();

const lectureContents = (
  nodes: readonly LectureTreeNode[],
  examId: string,
  activeKey: string
): McGrawHillContents => ({
  kind: 'lecture',
  examId,
  activeKey,
  chapters: mcgrawHillChapters(nodes, examId).map((chapter) => {
    const number = chapter.number === null ? null : Number(chapter.number);

    return {
      key: chapter.lectureId,
      number,
      title: chapter.name,
      sections: (number === null
        ? []
        : (MCGRAW_HILL_SUBCHAPTERS[number] ?? [])
      ).map(({ title, page }) => ({ label: title, page })),
    };
  }),
});

const referenceContents = (documentId: string): McGrawHillContents | null => {
  const book = mcgrawHillReferenceById(documentId)?.textbook?.book;
  if (!book) return null;

  return {
    kind: 'reference',
    activeKey: documentId,
    chapters: MCGRAW_HILL_REFERENCES.flatMap(({ id, textbook }) =>
      textbook?.book === book
        ? [
            {
              key: id,
              number: textbook.chapter,
              title: textbook.title,
              sections: [],
            },
          ]
        : []
    ).sort((a, b) => (a.number ?? 0) - (b.number ?? 0)),
  };
};

const isTextbookExam = (examId: string | undefined): examId is string =>
  MCGRAW_HILL_TEXTBOOK_EXAM_IDS.includes(examId ?? '');

/** The open document's contents for a McGraw Hill member, or null. */
const useContents = (
  current: ChatDocumentViewerCurrent | null
): McGrawHillContents | null => {
  const store = useContext(ReactReduxContext)?.store;
  const subscribe = store ? store.subscribe : noStoreSubscribe;
  const read = () => store?.getState() as Partial<RootState> | undefined;
  // The plain Ora view (ORA-1268) flips membership without a store change.
  usePlainOraViewFlag();

  const isMember = useSyncExternalStore(subscribe, () =>
    isProfileInInstance(read()?.user?.profile, INSTANCE_SLUGS.mcgrawHill)
  );

  const nodesById = useSyncExternalStore(
    subscribe,
    () => read()?.lectureTree?.nodesById
  );

  const source = current?.source;
  const lectureId = source?.kind === 'lecture' ? source.lectureId : null;
  const documentId = source?.kind === 'chatDocument' ? source.documentId : null;
  const storeNode = lectureId ? nodesById?.[lectureId] : undefined;
  const lectureExamId = current?.lectureExam?.examId;

  // A lecture outside the store's tree (Katzung's, with the handbook active)
  // is looked for in the textbook's own tree.
  const lookupExamId =
    isMember && lectureId && !storeNode
      ? isTextbookExam(lectureExamId)
        ? lectureExamId
        : MCGRAW_HILL_TEXTBOOK_EXAM_IDS[0]
      : null;

  const [fetched, setFetched] = useState<{
    examId: string;
    nodes: LectureTreeNode[] | null;
  } | null>(null);

  useEffect(() => {
    if (!lookupExamId) return;
    let isCurrent = true;

    void fetchTextbookTree(lookupExamId).then((nodes) => {
      if (isCurrent) setFetched({ examId: lookupExamId, nodes });
    });

    return () => {
      isCurrent = false;
    };
  }, [lookupExamId]);

  return useMemo(() => {
    if (!isMember) return null;
    if (documentId) return referenceContents(documentId);
    if (!lectureId) return null;

    if (storeNode) {
      return isTextbookExam(storeNode.examId) && nodesById
        ? lectureContents(Object.values(nodesById), storeNode.examId, lectureId)
        : null;
    }

    const nodes = fetched?.examId === lookupExamId ? fetched?.nodes : null;
    if (!lookupExamId || !nodes?.some((node) => node.id === lectureId)) {
      return null;
    }

    return lectureContents(nodes, lookupExamId, lectureId);
  }, [
    isMember,
    documentId,
    lectureId,
    storeNode,
    nodesById,
    fetched,
    lookupExamId,
  ]);
};

interface McGrawHillDocument {
  /** The viewer's title formatter ("Chapter 12 - <name>"), or undefined. */
  formatFileName: ((fileName: string) => string) | undefined;
  /** The book's contents rail beside the viewer, or null. */
  contents: ReactNode;
}

/**
 * mcgraw-hill: the open document's McGraw Hill chrome in the chat's document
 * workspace: the textbook title for a chapter and, for a chapter of a
 * textbook, the book's contents (see `McGrawHillContents`).
 */
export const useMcGrawHillDocument = (
  current: ChatDocumentViewerCurrent | null,
  viewerRef: RefObject<DocumentPdfViewerHandle | null>,
  onClose: () => void
): McGrawHillDocument => {
  const { open, openLectureSession } = useChatDocumentViewer();
  const contents = useContents(current);

  const activeTitle = useMcGrawHillDocumentTitle(
    current?.source.kind === 'lecture' ? current.source.lectureId : null
  );

  // A Katzung lecture is titled as a chapter whichever exam is active.
  const lectureName =
    contents?.kind === 'lecture'
      ? contents.chapters.find((chapter) => chapter.key === contents.activeKey)
          ?.title
      : undefined;

  const formatFileName =
    activeTitle ??
    (contents?.kind === 'lecture'
      ? (fileName: string) => mcgrawHillDocumentTitle(fileName, lectureName)
      : undefined);

  const rail =
    contents && contents.chapters.length > 0 ? (
      <McGrawHillChapterNav
        chapters={contents.chapters}
        activeKey={contents.activeKey}
        onSelectChapter={(key, page) =>
          contents.kind === 'lecture'
            ? openLectureSession(key, {
                exam:
                  current?.lectureExam ??
                  (contents.examId ? { examId: contents.examId } : null),
                page,
              })
            : open({ kind: 'chatDocument', documentId: key }, { page })
        }
        onSelectPage={(page) => viewerRef.current?.scrollToPage(page)}
        onClose={onClose}
      />
    ) : null;

  return { formatFileName, contents: rail };
};
